import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import type { AppointmentRecord } from "@/lib/types/appointment";
import { useAccessPolicy } from "@/hooks/access/useAccess";

interface AppointmentModalContextState {
  open: boolean;
  appointmentId: number | null;
  appointment: AppointmentRecord | null;
  setAppointment: (appt: AppointmentRecord | null) => void;
  updateAppointment: (changes: Partial<AppointmentRecord>) => void;
  openModal: (id: number, initialAppointment?: AppointmentRecord | null) => void;
  closeModal: () => void;
  resetAppointment: () => void;
}

const AppointmentModalContext = createContext<AppointmentModalContextState | undefined>(undefined);

export function useAppointmentModal() {
  const ctx = useContext(AppointmentModalContext);
  if (!ctx) {
    throw new Error("useAppointmentModal must be used within an AppointmentModalProvider");
  }
  return ctx;
}

interface AppointmentModalProviderProps {
  children: ReactNode;
}

export function AppointmentModalProvider({ children }: AppointmentModalProviderProps) {
  const [open, setOpen] = useState(false);
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const [appointment, setAppointment] = useState<AppointmentRecord | null>(null);
  const [initialAppointment, setInitialAppointment] = useState<AppointmentRecord | null>(null);
  const policy = useAccessPolicy();

  const deriveDurationFields = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return null;
    const parse = (t: string) => {
      const [h, m] = t.split(":").map((n) => Number(n));
      if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
      return h * 60 + m;
    };
    const startMin = parse(startTime);
    const endMinRaw = parse(endTime);
    if (startMin == null || endMinRaw == null) return null;
    let endMin = endMinRaw;
    if (endMin < startMin) endMin += 24 * 60; // handle overnight wrap
    const diff = endMin - startMin;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    // Use English format for i18n compatibility
    let durationStr = "0min";
    if (hours > 0 && minutes > 0) durationStr = `${hours}h ${minutes}min`;
    else if (hours > 0) durationStr = `${hours}h`;
    else if (minutes > 0) durationStr = `${minutes}min`;
    durationStr += ` (${startTime} – ${endTime})`;
    return {
      startTimeString: startTime,
      endTimeString: endTime,
      serviceDurationString: durationStr,
      durationHours: hours,
      durationMinutes: minutes,
    };
  };

  // Open modal and optionally set initial appointment data
  const openModal = useCallback((id: number, initialAppt?: AppointmentRecord | null) => {
    if (!policy.records.read) {
      return;
    }
    setAppointmentId(id);
    setOpen(true);
    if (initialAppt) {
      console.log("[AppointmentModalContext] openModal called with initial appointment:", initialAppt);
      setAppointment(initialAppt);
      setInitialAppointment(initialAppt);
    }
  }, [policy.records.read]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setAppointmentId(null);
    setAppointment(null);
    setInitialAppointment(null);
  }, []);

  // Update appointment with partial changes (deep merge for nested objects)
  const updateAppointment = useCallback((changes: Partial<AppointmentRecord>) => {
    setAppointment((prev) => {
      if (!prev) return prev;
      // Deep merge for nested objects (e.g., client)
      const merged: any = { ...prev }; // Fixed: was { .prev }
      for (const key in changes) {
        if (
          typeof changes[key] === "object" &&
          changes[key] !== null &&
          !Array.isArray(changes[key]) &&
          typeof merged[key] === "object" &&
          merged[key] !== null &&
          !Array.isArray(merged[key])
        ) {
          merged[key] = { ...merged[key], ...(changes as any)[key] }; // Fixed: was { .merged[key], .changes[key] }
        } else {
          merged[key] = changes[key];
        }
      }
      // derive duration labels if times changed
      const derived = deriveDurationFields(merged.startTime, merged.endTime);
      if (derived) {
        merged.detailsSection = { ...(merged.detailsSection || {}), ...derived };
      }
      // Logging
      console.log("[AppointmentModalContext] updateAppointment called with changes:", changes);
      console.log("[AppointmentModalContext] new appointment object:", merged);
      return merged;
    });
  }, []);

  // Reset appointment to initial state
  const resetAppointment = useCallback(() => {
    console.log("[AppointmentModalContext] resetAppointment called, resetting to initial appointment:", initialAppointment);
    setAppointment(initialAppointment);
  }, [initialAppointment]);

  const value: AppointmentModalContextState = {
    open,
    appointmentId,
    appointment,
    setAppointment,
    updateAppointment,
    openModal,
    closeModal,
    resetAppointment,
  };

  return (
    <AppointmentModalContext.Provider value={value}>
      {children}
    </AppointmentModalContext.Provider>
  );
}
